// apps/admin/src/hooks/use-keyboard-nudge.ts
//
// P5 UI Polish W2 T5 — Keyboard nudge (spec §3.2).
//
// Arrow = 1px / Shift+Arrow = 10px / Alt+Arrow = gridStep.
// Caller passes `enabled` (e.g., the composer editor page is mounted),
// selectedIds, gridStep, and onNudge(ids, dx, dy).

import { useEffect } from 'react';

export interface UseKeyboardNudgeArgs {
  enabled: boolean;
  selectedIds: ReadonlyArray<string>;
  gridStep: number;
  onNudge: (zoneIds: ReadonlyArray<string>, dx: number, dy: number) => void;
}

export function useKeyboardNudge(args: UseKeyboardNudgeArgs): void {
  const { enabled, selectedIds, gridStep, onNudge } = args;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent): void => {
      if (selectedIds.length === 0) return;
      // Don't intercept arrow keys when an input/select/textarea is focused
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase() ?? '';
      if (tag === 'input' || tag === 'select' || tag === 'textarea' || target?.isContentEditable) return;

      let dx = 0;
      let dy = 0;
      if (e.key === 'ArrowLeft') dx = -1;
      else if (e.key === 'ArrowRight') dx = 1;
      else if (e.key === 'ArrowUp') dy = -1;
      else if (e.key === 'ArrowDown') dy = 1;
      else return;

      e.preventDefault();
      const multiplier = e.altKey ? gridStep : e.shiftKey ? 10 : 1;
      onNudge(selectedIds, dx * multiplier, dy * multiplier);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, selectedIds, gridStep, onNudge]);
}
